import { MutableRefObject, useCallback, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { MediaCategory, MediaResource } from '../../../../system/media';
import { FeatureFlags } from 'src/flags';
import { useSyncedMediaResource } from '@brightmls/nextgen.lae.flc';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';

interface UseDragProps {
  selectedFiles: string[]; // Array of MED_VW_KEY values
  skipValidationRef: MutableRefObject<boolean>;
  setSelectedFiles: (files: string[] | ((prev: string[]) => string[])) => void;
}

interface DraggedItem {
  id: string; // The MEDIA_ITEM_NUMBER of the actively dragged item
  files: MediaResource[];
  indices: number[];
  medVwKeys: string[]; // Store the MED_VW_KEY values for filtering
}

export const useDrag = ({
  selectedFiles,
  skipValidationRef,
  setSelectedFiles,
}: UseDragProps) => {
  const [isDraggingMultiple, setIsDraggingMultiple] = useState(false);
  const [draggedItems, setDraggedItems] = useState<DraggedItem | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [draggedFromIndex, setDraggedFromIndex] = useState<number | null>(null);
  const [isOverMainPosition, setIsOverMainPosition] = useState(false);
  const { hasFeature } = useFeatureFlags();
  const isMultiDragEnabled = hasFeature(FeatureFlags.NGW_MULTI_DRAG_DROP);

  const { updateMedia: setLocalFiles, displayItems: files } =
    useSyncedMediaResource(MediaCategory.Photo);

  // Start a drag operation
  const startMultiDrag = useCallback(
    (activeId: string, currentSelectedFiles: string[]) => {
      let draggedFileObjects: MediaResource[];

      // If no files are selected, use the actively dragged file
      if (currentSelectedFiles.length === 0) {
        const draggedFile = files.find(
          (file) => file.MEDIA_ITEM_NUMBER?.toString() === activeId,
        );

        if (draggedFile) {
          draggedFileObjects = [draggedFile];
        } else {
          draggedFileObjects = [];
        }
      } else {
        // Multi-drag scenario - get all selected files
        draggedFileObjects = files.filter((file) =>
          currentSelectedFiles.includes(file.MED_VW_KEY),
        );
      }

      // Get indices of all dragged files
      const indices = draggedFileObjects.map((file) =>
        files.findIndex((f) => f.MED_VW_KEY === file.MED_VW_KEY),
      );

      // Store MED_VW_KEY values for filtering
      const medVwKeys = draggedFileObjects.map((f) => f.MED_VW_KEY);

      setIsDraggingMultiple(true);
      setDraggedItems({
        id: activeId,
        files: draggedFileObjects,
        indices,
        medVwKeys,
      });
    },
    [files],
  );

  // End the drag operation
  const endMultiDrag = useCallback(() => {
    setIsDraggingMultiple(false);
    setDraggedItems(null);
  }, []);

  // Calculate the new order after dropping
  const calculateNewOrder = useCallback(
    (overId: string): MediaResource[] | null => {
      if (!isDraggingMultiple || !draggedItems) return null;

      // Use draggedItems.medVwKeys instead of selectedFiles
      // This ensures we're using the captured state from when drag started
      const remainingFiles = files.filter(
        (file) => !draggedItems.medVwKeys.includes(file.MED_VW_KEY),
      );

      // Validate we removed the correct number of files
      if (files.length - remainingFiles.length !== draggedItems.files.length) {
        console.error(
          '[useDrag] Mismatch in filtering - potential data corruption',
        );
        return null;
      }

      const validDropTargets = getValidDropTargets(files);

      // Find where we're dropping in the validDropTargets array
      const dropPositionIndex = validDropTargets.findIndex(
        (f) => f.MEDIA_ITEM_NUMBER?.toString() === overId,
      );

      if (dropPositionIndex === -1) {
        console.error('[useDrag] Drop target not found in validDropTargets');
        return null;
      }

      // Insert the dragged files at the calculated position
      const newOrder = [
        ...remainingFiles.slice(0, dropPositionIndex),
        ...draggedItems.files,
        ...remainingFiles.slice(dropPositionIndex),
      ];

      // Validate no duplicates
      const medVwKeys = newOrder.map((f) => f.MED_VW_KEY);
      const uniqueKeys = new Set(medVwKeys);
      if (uniqueKeys.size !== medVwKeys.length) {
        console.error(
          '[useDrag] Duplicate files detected - preventing data corruption',
        );
        return null;
      }

      // Update display order values
      return newOrder.map((file, index) => ({
        ...file,
        MEDIA_DISPLAY_ORDER: index,
      }));
    },
    [isDraggingMultiple, draggedItems, files],
  );

  // Filter files for multi-drag: keep actively dragged file, remove other selected files
  const getValidDropTargets = useCallback(
    (filesToFilter: MediaResource[]) => {
      if (!isDraggingMultiple || !draggedItemId || !draggedItems) {
        return filesToFilter;
      }

      return filesToFilter.filter((file) => {
        // Keep the actively dragged file (for placeholder)
        if (file.MEDIA_ITEM_NUMBER.toString() === draggedItemId) {
          return true;
        }
        // Filter out other dragged files
        return !draggedItems.medVwKeys.includes(file.MED_VW_KEY);
      });
    },
    [isDraggingMultiple, draggedItemId, draggedItems],
  );

  // Handle drag start event
  const handleDragStart = useCallback(
    (event: any) => {
      const { active } = event;
      const activeId = active.id.toString();

      if (isMultiDragEnabled) {
        // Find the dragged file to get its MED_VW_KEY
        const draggedFile = files.find(
          (f) => f.MEDIA_ITEM_NUMBER.toString() === activeId,
        );

        // Determine what to drag
        let currentSelectedFiles: string[];

        if (draggedFile && !selectedFiles.includes(draggedFile.MED_VW_KEY)) {
          if (selectedFiles.length > 0) {
            // Drag just this one item, but keep selections intact
            currentSelectedFiles = [draggedFile.MED_VW_KEY];
          } else {
            // No selections, just drag this one item
            currentSelectedFiles = [draggedFile.MED_VW_KEY];
          }
        } else {
          // Dragged file is in selections OR is the only file being dragged
          currentSelectedFiles =
            selectedFiles.length > 0
              ? [...selectedFiles]
              : draggedFile
                ? [draggedFile.MED_VW_KEY]
                : [];
        }

        startMultiDrag(activeId, currentSelectedFiles);
        setDraggedItemId(activeId);
      } else {
        // Multi-drag feature not enabled, use normal single drag
        setDraggedItemId(activeId);

        const fromIndex = files.findIndex(
          (f) => f.MEDIA_ITEM_NUMBER.toString() === activeId,
        );
        setDraggedFromIndex(fromIndex);
      }
    },
    [files, isMultiDragEnabled, startMultiDrag, selectedFiles],
  );

  // Handle drag over event
  const handleDragOver = useCallback(
    (event: any) => {
      const { active, over } = event;
      const validDropTargets = getValidDropTargets(files);

      // Check for null/undefined BEFORE accessing properties
      if (!active) {
        setIsOverMainPosition(false);
        return;
      }

      if (!over) {
        setIsOverMainPosition(false);
        return;
      }

      // Now safe to access active.id and over.id
      const activeIndex = files.findIndex(
        (f) => f.MEDIA_ITEM_NUMBER.toString() === active.id.toString(),
      );
      const overIndex = validDropTargets.findIndex(
        (f) => f.MEDIA_ITEM_NUMBER.toString() === over.id.toString(),
      );

      setIsOverMainPosition(overIndex === 0 && activeIndex !== 0);
    },
    [files, draggedFromIndex, isMultiDragEnabled, isDraggingMultiple],
  );

  // Handle drag cancel event (when ESC pressed or drag cancelled)
  const handleDragCancel = useCallback(() => {
    endMultiDrag();
    setDraggedItemId(null);
    setDraggedFromIndex(null);
    setIsOverMainPosition(false);
    // Don't clear selections on cancel
  }, [endMultiDrag]);

  // Handle drag end event
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;

      // Now safe to access active.id and over.id
      const activeIndex = files.findIndex(
        (f) => f.MEDIA_ITEM_NUMBER.toString() === active.id.toString(),
      );
      const overIndex = getValidDropTargets(files).findIndex(
        (f) => f.MEDIA_ITEM_NUMBER.toString() === over.id.toString(),
      );
      // Check if this was a valid drop or a cancellation
      const wasValidDrop = active && over && activeIndex !== overIndex;

      // Determine if we should clear selections
      // Only clear if we dragged the selected items (not an unselected item)
      const shouldClearSelections =
        wasValidDrop &&
        draggedItems &&
        selectedFiles.length > 0 &&
        draggedItems.medVwKeys.some((key) => selectedFiles.includes(key));

      // Clear drag states
      endMultiDrag();
      setDraggedItemId(null);
      setDraggedFromIndex(null);
      setIsOverMainPosition(false);

      // Only clear selections if we dragged selected items
      if (shouldClearSelections) {
        setSelectedFiles([]);
      }

      if (!active || !over || activeIndex === overIndex) {
        return;
      }

      if (isMultiDragEnabled && isDraggingMultiple) {
        // Handle multi-drag drop
        const newOrder = calculateNewOrder(over.id.toString());
        if (newOrder) {
          // Set skipValidation flag
          skipValidationRef.current = true;

          setLocalFiles(newOrder);

          setTimeout(() => {
            skipValidationRef.current = false;
          }, 500);
        }

        return;
      }

      // Set skipValidation flag to prevent interruptions
      skipValidationRef.current = true;

      try {
        setLocalFiles((prevFiles: any[]) => {
          const sortedFiles = [...prevFiles].sort(
            (a, b) => a.MEDIA_DISPLAY_ORDER - b.MEDIA_DISPLAY_ORDER,
          );

          const oldIndex = sortedFiles.findIndex(
            (f) => f.MEDIA_ITEM_NUMBER.toString() === active.id.toString(),
          );
          const newIndex = sortedFiles.findIndex(
            (f) => f.MEDIA_ITEM_NUMBER.toString() === over.id.toString(),
          );

          if (oldIndex === -1 || newIndex === -1) {
            return prevFiles;
          }

          const newFiles = arrayMove(sortedFiles, oldIndex, newIndex);

          const updatedFiles = newFiles.map((file, index) => ({
            ...file,
            MEDIA_DISPLAY_ORDER: index,
          }));

          updatedFiles.forEach((file: any) => {
            if (file.MEDIA_KEY === null) {
              console.error(
                `MEDIA_KEY is null after drag end Files: ${JSON.stringify(file)}`,
              );
              throw new Error(
                `MEDIA_KEY is null after drag end Files: ${JSON.stringify(file)}`,
              );
            }
          });

          return updatedFiles;
        });
      } catch (error) {
        console.error('Error handling drag end operation:', error);
      } finally {
        setTimeout(() => {
          skipValidationRef.current = false;
        }, 500);
      }
    },
    [
      isMultiDragEnabled,
      isDraggingMultiple,
      draggedItems,
      calculateNewOrder,
      endMultiDrag,
      setSelectedFiles,
      setLocalFiles,
      skipValidationRef,
      selectedFiles,
      files,
    ],
  );

  return {
    isDraggingMultiple,
    draggedItems,
    draggedItemId,
    isOverMainPosition,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    getValidDropTargets,
  };
};
